<template>
  <div id="app">
    <div class="container">
      <div class="m-block m-block--code m-block--full">
        <div class="m-block__content">
          <div class="m-block__code f-rte f-rte--block">
            <ControlGroup>
              <DataGetter
                :url="`${apiRoute}/countries/region`"
                :params="{ showAll: true, tags: 'EU4Energy' }"
                v-slot="{ data }"
                @fetched="setCountries"
                :child-element-type="null"
              >
                <Multiselect
                  v-if="data"
                  label="Country/region"
                  :options="
                    data.map((region) => {
                      return {
                        label: region.name,
                        value: region.id,
                        options: region.members.map((country) => ({
                          label: country.name,
                          value: country.stats,
                        })),
                      };
                    })
                  "
                  :default-option="selectedCountriesLabel"
                  v-model="selectedCountries"
                />
              </DataGetter>
              <DataGetter
                v-if="selectedCountries.length > 0"
                :url="`${apiRoute}/stats/indicators`"
                :params="{ countries: useCountries, year }"
                :child-element-type="null"
                v-slot="{ data }"
                @fetched="setAvailableIndicators"
              >
                <Dropdown
                  v-if="data"
                  label="Indicator"
                  :options="indicatorOptions"
                  option-label="name"
                  option-value="id"
                  v-model="selectedIndicatorId"
                />
              </DataGetter>
              <Dropdown label="Year" :options="years" v-model="year" />
            </ControlGroup>
            <!-- <ControlGroup>
              <Toggle v-model="percentage" label="Percentage" />
            </ControlGroup> -->
            <DataGetter
              v-if="selectedCountries.length > 0 && selectedIndicatorId"
              :url="`${apiRoute}/stats/indicator/${selectedIndicatorId}`"
              :params="{
                countries: useCountries,
                year,
                startYear: !year ? 2000 : undefined,
              }"
              v-slot="{ data }"
              class="container"
              @fetched="setUnits"
            >
              <!-- use a single year comparison -->
              <template v-if="year">
                <DataSlicer
                  :data="data"
                  x-axis-dimension="country"
                  y-axis-dimension="seriesLabel"
                  value-dimension="value"
                  v-slot="{ slicedData }"
                  class="highcharts-container"
                >
                  <Chart
                    v-if="slicedData.length > 0"
                    :options="{
                      ...chartOptions,
                      series: slicedData.map((series) => ({
                        ...series,
                        data: Array.isArray(series.data)
                          ? series.data.map((point) => [
                              getCountryNameById(point[0]),
                              point[1],
                            ])
                          : [
                              {
                                name: getCountryNameById(selectedCountries[0]),
                                x: 0,
                                y: series.data,
                              },
                            ],
                      })),
                    }"
                    :key="
                      `allcountries${selectedIndicatorId}${selectedCountries.join(
                        ''
                      )}chart${year}`
                    "
                    class="highcharts-outline-black m-chart__item"
                  />
                  <p v-else class="no-data">
                    No data available for this year and indicator.
                  </p>
                </DataSlicer>
              </template>
              <!-- if showing all years, use small multiples -->
              <template v-else>
                <div
                  class="chart-group"
                  :class="{
                    'chart-group--small': selectedCountries.length > 2,
                  }"
                >
                  <DataSlicer
                    v-for="country in useCountries"
                    :data="data"
                    :key="`${country}slicer`"
                    x-axis-dimension="year"
                    y-axis-dimension="seriesLabel"
                    :filters="{
                      short: [country],
                    }"
                    value-dimension="value"
                    :ignore-nulls="true"
                    v-slot="{ slicedData }"
                    class="chart-group__chart"
                    @sliced="setMax"
                  >
                    <template v-if="slicedData.length > 0">
                      <Sparkline
                        size="normal"
                        :title="getCountryNameById(country)"
                        :series="
                          slicedData.map((series) => ({
                            ...series,
                            pointStart: series.data[0][0],
                            data: series.data.map((row) => row[1]),
                            stacking: selectedIndicator.dontStack
                              ? undefined
                              : 'normal',
                            colorIndex:
                              shownSeries.findIndex((s) => s === series.name) %
                              10,
                          }))
                        "
                        :key="
                          `${country}${selectedIndicatorId}chart${
                            selectedCountries.length / 2 > 2 ? 'small' : 'big'
                          }`
                        "
                        showXAxis
                        :options="{
                          chart: {
                            type: selectedIndicator.dontStack ? 'line' : 'area',
                          },
                          yAxis: {
                            max,
                            plotLines: [{ value: max }],
                          },
                          tooltip: {
                            valueSuffix: units == '%' ? ` ${units}` : units,
                          },
                        }"
                      />
                    </template>
                    <template v-else>
                      <div class="sparkline no-data">
                        No data for {{ getCountryNameById(country) }}
                      </div>
                    </template>
                  </DataSlicer>
                </div>
                <div class="m-chart__footer">
                <ChartLegend
                  :legend-items="
                    shownSeries.map((name, i) => ({ name, colorIndex: i % 10 }))
                  "
                  :hover-series="() => {}"
                />
                </div>
              </template>
            </DataGetter>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global Highcharts */
import {
  DataGetter,
  Dropdown,
  Multiselect,
  ControlGroup,
  // Toggle,
  DataSlicer,
} from "@international-energy-agency/vue-ui";
import { Chart } from "highcharts-vue";
import {
  Sparkline,
  ChartLegend,
} from "@international-energy-agency/vue-ui/highcharts";
import ChartOptions from "@international-energy-agency/vue-ui/highcharts";
Highcharts.setOptions({
  lang: {
    numericSymbols: null,
    locale: 'fr'
  },
});
export default {
  name: "App",
  components: {
    DataGetter,
    Dropdown,
    Multiselect,
    ControlGroup,
    // Toggle,
    DataSlicer,
    Chart,
    Sparkline,
    ChartLegend,
  },
  props: {
    test: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const years = [
      {
        label: "Since 2000",
        value: null,
      },
    ];
    for (let i = 2022; i > 1989; i -= 1) {
      years.push({
        label: i,
        value: i,
      });
    }
    return {
      regions: [],
      countries: [],
      selectedCountries: [],
      availableIndicators: [],
      selectedIndicatorId: null,
      defaultIndicator: "TESbySource",
      indicatorData: [],
      year: 2022,
      years,
      percentage: false,
      units: "",
      max: 0,
      updateArgs: [true, true, true],
      indicatorGroups: [
        {
          label: "Key indicators",
          indicators: [
            "TESbySource",
            "TFCbySource",
            "TFCbySector",
            "SDG72",
            "SDG73",
            "SDG94",
          ],
        },
        {
          label: "Electricity and heat",
          indicators: [
            "ElecGenByFuel",
            "ElecImportsExports",
            "ElecConsBySector",
            "HeatGenByFuel",
          ],
        },
        {
          label: "Emissions",
          indicators: ["CO2BySector", "CO2BySource", "emissionsDrivers"],
        },
        {
          label: "Fossil fuels",
          indicators: [
            "CoalProdByType",
            "CoalImportsExports",
            "CoalConsBySector",
            "OilProd",
            "PrimaryOilImportsExports",
            "SecondaryOilImportsExports",
            "OilProductsConsBySector",
            "OilProductsCons",
            "NatGasProd",
            "NatGasImportsExports",
            "NatGasConsBySector",
          ],
        },
      ],
    };
  },
  watch: {
    selectedIndicatorId(oldVal, newVal) {
      if (oldVal !== newVal) this.max = 0;
    },
  },
  computed: {
    apiRoute() {
      const { test } = this;
      return `https://${test ? 'api.test' : 'api'}.iea.org`;
    },
    chartOptions() {
      const {
        percentage,
        selectedIndicator,
        showMultiple,
        year,
        smallMultipleOptions,
        units,
      } = this;
      const dontStack = selectedIndicator
        ? selectedIndicator.dontStack
        : !showMultiple;
      const stacking = dontStack
        ? undefined
        : percentage
        ? "percent"
        : "normal";
      return ChartOptions.buildOptions(
        {
          tooltip: {
            valueSuffix: ` ${units}`,
          },
          xAxis: year
            ? {
                type: "category",
              }
            : {},
          plotOptions: {
            area: {
              stacking,
              label: {
                enabled: true,
              },
            },
            bar: {
              stacking,
            },
          },
          ...(year ? {} : smallMultipleOptions),
        },
        year ? "bar" : dontStack ? "line" : "area"
      );
    },
    selectedIndicator() {
      return this.availableIndicators.find(
        (indicator) => indicator.id === this.selectedIndicatorId
      );
    },
    indicatorOptions() {
      const { availableIndicators } = this;
      return this.indicatorGroups.reduce(
        (options, group) => [
          ...options,
          {
            name: group.label,
            disabled: true,
            options: group.indicators.map((id) =>
              availableIndicators.find((indicator) => indicator.id === id)
            ),
          },
        ],
        []
      );
    },
    smallMultipleOptions() {
      const { selectedCountries, units, percentage } = this;
      return {
        chart: {
          height: selectedCountries.length > 2 ? 250 : 350,
        },
        yAxis: {
          plotLines: [
            { value: 0, className: "highcharts-zero-axis", zIndex: 2 },
          ],
          labels: {
            format: `{text} ${percentage ? "%" : units}`,
            align: "left",
            x: 1,
            y: 12,
          },
          maxPadding: 0,
          minTickInterval: 0,
          tickAmount: 1,
          startOnTick: false,
          endOnTick: true,
          showLastLabel: true,
          showFirstLabel: false,
          softMin: 0
        },
        xAxis: {
          min: 1990,
          max: 2019,
          tickPositions: [1990, 2019],
          showLastLabel: true,
          endOnTick: true,
          tickLength: 5,
          labels: {
            formatter: function() {
              if (this.isFirst || this.isLast) {
                return this.value;
              }
            },
          },
        },
        legend: {
          enabled: false,
        },
      };
    },
    showMultiple() {
      const { indicatorData } = this;
      const flows = new Set();
      const products = new Set();
      for (let i = 0; i < indicatorData.length; i += 1) {
        const dataRow = indicatorData[i];
        if (i === 0) {
          if (dataRow.flow) flows.add(dataRow.flow);
          if (dataRow.product) products.add(dataRow.product);
          continue;
        }
        if (
          (dataRow.flow && !flows.has(dataRow.flow)) ||
          (dataRow.product && !products.has(dataRow.product))
        ) {
          return true;
        }
      }
      return false;
    },
    shownSeries() {
      return this.indicatorData.reduce((series, row) => {
        if (!series.includes(row.seriesLabel)) series.push(row.seriesLabel);
        return series;
      }, []);
    },
    singleYearIndicatorData() {
      return this.indicatorData.filter(
        (row) => row.year === this.year.toString()
      );
    },
    selectedCountriesLabel() {
      if (this.selectedCountries.length === 0)
        return "Select a country or region";
      return this.selectedCountries.map(this.getCountryNameById).join(", ");
    },
    useCountries() {
      return this.selectedCountries.reduce((countries, countryOrRegionID) => {
        const region = this.regions.find(
          (region) => region.id === countryOrRegionID
        );
        if (region) {
          return [
            ...countries,
            ...region.members.map((country) => country.stats),
          ];
        }
        return [...countries, countryOrRegionID];
      }, []);
    },
    indicatorDataByCountry() {
      return this.selectedCountries.reduce((countryData, countryId) => {
        countryData[countryId] = this.indicatorData.filter(
          (row) => row.country === countryId
        );
        return countryData;
      }, {});
    },
    ready() {
      return (
        this.countries.length > 0 &&
        this.selectedIndicatorId &&
        this.selectedCountries.length > 0
      );
    },
  },
  methods: {
    setCountries(regions) {
      this.countries = regions.reduce(
        (countries, region) => [
          ...countries,
          ...region.members.filter(
            (country) =>
              countries.findIndex((c) => c.stats === country.stats) === -1
          ),
        ],
        []
      );
      this.regions = regions;
    },
    setAvailableIndicators(indicators) {
      this.availableIndicators = indicators;
      if (!this.selectedIndicatorId)
        this.selectedIndicatorId = this.defaultIndicator;
    },
    setUnits(data) {
      this.units = data.length > 0 ? data[0].units : this.units;
      this.indicatorData = data;
    },
    getCountryNameById(id) {
      const region = this.regions.find((region) => region.id === id);
      if (region) return region.name;
      const country = this.countries.find(
        (country) => country.iso3 === id || country.stats === id
      );
      return country ? country.fullName : "";
    },
    setMax(data) {
      const countryMax =
        this.selectedIndicator.dontStack || data.length === 1
          ? // get the largest item in a single series, or largest item among several unstacked series
            data.reduce((max, series) => {
              const seriesMax = series.data.reduce(
                (seriesMax, row) => (row[1] > seriesMax ? row[1] : seriesMax),
                0
              );
              return seriesMax > max ? seriesMax : max;
            }, 0)
          : // get the largest stacked total of any year
            data[0].data
              .map((row) => row[0])
              .reduce((max, year) => {
                const yearTotal = data.reduce(
                  (total, series) =>
                    total + series.data.find((row) => row[0] === year)[1],
                  0
                );
                return yearTotal > max ? yearTotal : max;
              }, 0);
      // increase max if necessary
      this.max = countryMax > this.max ? countryMax : this.max;
    },
  },
};
</script>

<style>
.chart-group {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-content: flex-start;
  /* height: 800px; */
}
/*
.chart-group .chart-group__chart {
  min-width: 45%;
  max-width: 45%;
  border: 1px solid #e6e6e6;
}
.chart-group--small .chart-group__chart {
  min-width: 300px;
}
*/
.sparkline {
  border-radius: 8px;
  padding: 8px 4px;
  margin: 8px;
  border: 1px solid #ececec;
}
.no-data {
  min-height: 170px;
  padding: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #949494;
}
.chart-group__chart {
  position: relative;
  flex: 0 0 23%;
  margin: 8px;
  height: max-content;
  /* background: #e6e6e6; */
}
.chart-group__chart .highcharts-grid-line {
  stroke: #000000;
}
.chart-group__chart .highcharts-background {
  fill: transparent;
}
.chart-group__chart-title {
  cursor: pointer;
  z-index: 1;
  font-size: 14px;
  padding-left: 10px;
  padding-top: 10px;
  font-weight: 600;
  position: absolute;
  top: 0;
}
.a-dropdown li {
  margin-bottom: 0px;
}
.a-dropdown ul > ul {
  margin-bottom: 8px !important;
}
</style>
